import { account } from '../core/account.js';
import { catalog } from '../core/catalog.js';
import { openOrderEditor } from './order-editor.js';
import { customOrder } from '../core/custom-order.js';
import { applyGroupOrder, flattenGroups } from '../core/order-core.js';
import { groupByCategory } from './render.js';
import { filters } from '../core/filters.js';
import { settings } from '../core/storage.js';
import { t } from '../core/i18n.js';
import { qualityOf, resolutionOf, cleanName, normalizeText } from '../core/utils.js';
import { providerPickerItems } from '../core/provider-groups.js';
import { createCountryFilter } from './country-filter.js';
import { openCategoryPicker, sortByWatched } from './render.js';

/* La selección de TV en directo (ámbito, proveedores, categorías, país, calidad y orden) en un solo sitio: la usan la pantalla
   de TV en directo y la Guía de TV, así que lo que se elige en una es lo que se ve en la otra. Todo se guarda con `filters` y
   `settings` (el mismo almacén), por proveedor y espectador. */

export const PROV = 'prov:'; // en el selector «Mostrar», un proveedor es 'prov:<id>' (se pueden elegir varios)
export const ADD_PROVIDER = '__add-provider';
export const QUALITIES = [
    { id: 'UHD', name: '4K' },
    { id: 'FHD', name: 'Full HD' },
    { id: 'HD', name: 'HD' },
    { id: 'SD', name: 'SD' }
];
// Resoluciones concretas que anuncian los nombres de canal (aparte de los grupos de arriba): solo salen en la lista si hay canales con ellas.
export const RESOLUTIONS = [576, 480, 360, 240];
const resId = (n) => 'R' + n;
const isRes = (id) => /^R\d+$/.test(String(id));
export const matchesQuality = (name, id) => (isRes(id) ? resolutionOf(name) === Number(String(id).slice(1)) : qualityOf(name) === id);

export const SORTS = () => [
    { id: 'default', name: t('live.sortDefault'), icon: 'format_list_numbered' },
    { id: 'name', name: t('movies.sortName'), icon: 'sort_by_alpha' },
    { id: 'watched', name: t('common.sortWatched'), icon: 'history' },
    { id: 'mine', name: t('sort.mine'), icon: 'drag_indicator' },
    { id: 'mine-edit', name: t('sort.mineEdit'), icon: 'edit' }
];

/**
 * `routeCat`: categoría que viene en la dirección (p. ej. «Ver todo» de una fila); si hay, se parte de ella en vez de lo guardado.
 * Devuelve el modelo: estado, filtro de país y operaciones (barra de filtros, canales, selectores).
 */
export function createLiveFilters({ routeCat = '' } = {}) {
    let model = null;
    const read = () => {
        const saved = routeCat ? {} : filters.get('live');
        const rc = routeCat || 'all';
        const state = {
            // Ámbito (todo / favoritos / recientes) separado de la categoría, como en el diseño móvil.
            scope: rc === 'fav' || rc === 'recent' ? rc : (saved.scope || 'all').startsWith(PROV) ? 'all' : saved.scope || 'all',
            // Proveedores elegidos (varios a la vez); vacío = todos. Antes se guardaba uno como ámbito «prov:<id>».
            providers: Array.isArray(saved.providers) ? saved.providers.map(String) : (saved.scope || '').startsWith(PROV) ? [saved.scope.slice(PROV.length)] : [],
            // Varias categorías a la vez; vacío = todas. `wanted` es lo elegido; `cats`, lo que de eso existe ahora mismo.
            wanted: rc === 'fav' || rc === 'recent' || rc === 'all' ? (saved.cats || []).map(String) : [String(rc)],
            cats: [],
            sort: settings.get('liveSort') || 'default',
            quality: QUALITIES.some((q) => q.id === saved.quality) || RESOLUTIONS.some((n) => resId(n) === saved.quality) ? saved.quality : 'all'
        };
        return { state, country: createCountryFilter('live', 'live', saved) };
    };
    model = read();
    let catList = [];

    const scopeList = () => {
        const list = [{ id: 'all', name: t('common.all'), icon: 'apps' }];
        // Favoritos y Recientes son excluyentes (eligen uno y se cierra); los proveedores se pueden marcar varios.
        if (catalog.favoriteItems('live').length) list.push({ id: 'fav', name: t('live.favorites'), icon: 'favorite', exclusive: true });
        if (catalog.recentChannels().length) list.push({ id: 'recent', name: t('live.recent'), icon: 'history', exclusive: true });
        // Con varios proveedores activos, también se puede ver solo lo de los elegidos.
        providerPickerItems(catalog.providerChoices({ kind: 'live' }), (p) => PROV + p.id).forEach((x) => list.push(x));
        // Atajo para añadir otra fuente sin ir a Ajustes (abre el formulario completo).
        if (!account.demo) list.push({ id: ADD_PROVIDER, name: t('settings.addProfile'), icon: 'add', exclusive: true }); // la demo pública no añade fuentes
        return list;
    };
    const qualityList = () => {
        const present = new Set();
        (catalog.live || []).forEach((c) => { const r = resolutionOf(c.name); if (r) present.add(r); });
        const extra = RESOLUTIONS.filter((n) => present.has(n) || resId(n) === model.state.quality).map((n) => ({ id: resId(n), name: n + 'p', icon: 'high_quality' }));
        return [{ id: 'all', name: t('common.all'), icon: 'apps' }].concat(QUALITIES.map((q) => ({ id: q.id, name: q.name, icon: 'high_quality' })), extra);
    };

    const api = {
        get state() { return model.state; },
        get country() { return model.country; },
        get catList() { return catList; },
        /** Vuelve a leer lo guardado (otra pantalla pudo cambiarlo mientras esta estaba en segundo plano). */
        reload() { model = read(); },

        /** Los canales que salen con la selección (sin buscador): los mismos en TV en directo y en la Guía. */
        items() {
            const { state, country } = model;
            let items;
            if (state.scope === 'fav') items = catalog.favoriteItems('live');
            else if (state.scope === 'recent') items = catalog.recentChannels();
            else items = catalog.byCategories('live', state.cats);
            if (state.providers.length) items = items.filter((c) => state.providers.some((id) => catalog.inProvider(c, id)));
            if (state.scope !== 'all' && state.cats.length) items = items.filter((c) => state.cats.indexOf(String(c.categoryId)) !== -1);
            items = country.apply(items);
            if (state.quality !== 'all') items = items.filter((c) => matchesQuality(c.name, state.quality));
            if (state.sort === 'name') items = items.slice().sort((a, b) => cleanName(a.name).localeCompare(cleanName(b.name), undefined, { numeric: true, sensitivity: 'base' }));
            if (state.sort === 'watched') items = sortByWatched(items, catalog.watchedAt('live'));
            // «Mi orden»: las hileras y los canales en el orden que eligió la persona (ver ui/order-editor.js).
            if (state.sort === 'mine') items = flattenGroups(applyGroupOrder(groupByCategory(items, catalog.categories.live, null), customOrder.get('live')));
            return items;
        },

        /** Pone al día lo elegido frente al catálogo y devuelve las entradas de la barra de filtros y los datos del orden. */
        bar() {
            const { state, country } = model;
            country.sync();
            const cats = country.categories(catalog.categories.live
                .filter((c) => !(catalog.lockActive && catalog.isLockedCategory('live', c.id))))
                .map((c) => ({ id: c.id, name: c.name, count: c.count }));
            const extra = [];
            if (catalog.lockActive && !catalog.isKids) extra.push({ id: 'unlock', name: t('common.unlock'), icon: 'lock', exclusive: true });
            catList = [{ id: 'all', name: t('common.all'), icon: 'apps' }].concat(extra, cats);
            const scopes = scopeList();
            if (!scopes.some((x) => x.id === state.scope)) state.scope = 'all';
            const choices = catalog.providerChoices({ kind: 'live' });
            state.providers = state.providers.filter((id) => choices.some((p) => p.id === id));
            const scope = scopes.find((x) => x.id === state.scope) || scopes[0];
            // Texto del botón «Mostrar»: el ámbito (si no es «Todo») y/o los proveedores elegidos.
            const provName = state.providers.length === 1 ? (choices.find((p) => p.id === state.providers[0]) || {}).name : state.providers.length > 1 ? t('live.nProviders', { n: state.providers.length }) : '';
            const showValue = state.scope !== 'all' ? (provName ? scope.name + ' · ' + provName : scope.name) : (provName || t('common.all'));
            const showIcon = state.scope !== 'all' ? scope.icon : state.providers.length ? 'dns' : 'apps';
            state.cats = state.wanted.filter((id) => cats.some((c) => String(c.id) === id));
            const cat = { name: state.cats.length === 0 ? t('common.all') : state.cats.length === 1 ? (cats.find((c) => String(c.id) === state.cats[0]) || {}).name : t('common.nCategories', { n: state.cats.length }) };
            const quality = qualityList().find((q) => q.id === state.quality) || qualityList()[0];
            const sort = SORTS().find((x) => x.id === state.sort) || SORTS()[0];
            const chips = [
                { key: 'scope', icon: showIcon, label: t('live.scope'), value: showValue, active: state.scope !== 'all' || state.providers.length > 0, compactValue: true },
                country.chip(),
                { key: 'cat', icon: 'category', label: t('common.category'), value: cat.name, active: state.cats.length > 0 },
                { key: 'quality', icon: 'high_quality', label: t('live.quality'), value: quality.name, active: state.quality !== 'all' },
                // En móvil el orden va en esta misma fila (el botón de la barra de herramientas se oculta); en pantalla ancha se oculta este.
                { key: 'sort', icon: 'swap_vert', label: t('live.sortBy'), value: sort.name, active: state.sort !== 'default', compactValue: true }
            ].filter(Boolean);
            return { chips, sort, sortActive: state.sort !== 'default' };
        },

        /** Abre el selector de un filtro; `done()` se llama cuando se ha cambiado algo (hay que repintar). */
        open(key, done) {
            const { state, country } = model;
            if (key === 'sort') {
                openCategoryPicker({
                    title: t('live.sortBy'),
                    list: SORTS(),
                    active: state.sort,
                    onSelect: (picked) => {
                        if (picked === 'mine-edit') { openOrderEditor({ section: 'live', items: () => api.items(), onClose: ({ changed }) => { if (changed) { state.sort = 'mine'; settings.set('liveSort', 'mine'); } done(); } }); return; }
                        state.sort = picked; settings.set('liveSort', picked); done();
                    }
                });
            } else if (key === 'scope') {
                openCategoryPicker({
                    title: t('live.scope'),
                    list: scopeList(),
                    multi: true,
                    active: (state.scope !== 'all' ? [state.scope] : []).concat(state.providers.map((id) => PROV + id)),
                    onSelect: (picked) => {
                        if (picked === ADD_PROVIDER) { window.IPTV.openAddSource(); return; }
                        if (Array.isArray(picked)) {
                            // Marcas del selector: el ámbito (si sigue marcado) y los proveedores.
                            state.scope = picked.indexOf('fav') !== -1 ? 'fav' : picked.indexOf('recent') !== -1 ? 'recent' : 'all';
                            state.providers = picked.filter((x) => x.indexOf(PROV) === 0).map((x) => x.slice(PROV.length));
                        } else {
                            state.scope = picked; // Favoritos o Recientes (excluyentes)
                        }
                        filters.set('live', { scope: state.scope, providers: state.providers });
                        done();
                    }
                });
            } else if (key === 'cat') {
                openCategoryPicker({
                    title: t('common.categories'),
                    list: catList,
                    active: state.cats,
                    multi: true,
                    onSelect: async (picked) => {
                        if (picked === 'unlock') {
                            if (await window.IPTV.actions.askUnlock()) done({ unlocked: true });
                            return;
                        }
                        state.wanted = Array.isArray(picked) ? picked.map(String) : [];
                        filters.set('live', { cats: state.wanted });
                        done();
                    }
                });
            } else if (key === 'country') {
                country.open(done);
            } else if (key === 'quality') {
                openCategoryPicker({
                    title: t('live.quality'),
                    list: qualityList(),
                    active: state.quality,
                    onSelect: (picked) => { state.quality = picked; filters.set('live', { quality: picked }); done(); }
                });
            }
        },

        /** Filtra por texto (el buscador de TV en directo) los canales ya elegidos. */
        search(items, q) {
            const text = normalizeText(q || '');
            return text ? items.filter((c) => c._s.indexOf(text) !== -1) : items;
        }
    };
    return api;
}
